import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";

import { BankingDataChart } from "@krea/lender-features/components/banking-data-chart";
import { formatMoneyAmount } from "@krea/lender-features/utils/formatMoneyAmount";

import type {
  RepaymentCapacityProfitLossEntry,
  RepaymentCapacityRatioEntry,
} from "./types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

type FinancialYearChartsProps = {
  profitLossEntries: RepaymentCapacityProfitLossEntry[];
  ratioEntries: RepaymentCapacityRatioEntry[];
  currency: string;
  applyingCompanyId?: string;
  hasBankingData?: boolean;
};

const revenueEarningsOptions = (currency: string): ChartOptions<"line"> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
      align: "start",
      labels: {
        boxHeight: 0,
        boxWidth: 17,
        font: { size: 12 },
        color: "#000",
        padding: 15,
      },
    },
  },
  scales: {
    x: {
      grid: { display: true, drawTicks: false, color: "#F0F0F0" },
      ticks: { padding: 10 },
    },
    y: {
      grid: { display: true, drawTicks: false, color: "#F0F0F0" },
      ticks: {
        callback: (value) =>
          formatMoneyAmount(Number(value), { digits: 1, currency }),
        padding: 10,
      },
    },
  },
});

export const FinancialYearCharts = ({
  profitLossEntries,
  ratioEntries,
  currency,
  applyingCompanyId,
  hasBankingData,
}: FinancialYearChartsProps) => {
  // Sort oldest → newest so charts read left-to-right chronologically
  const sortedEntries = useMemo(
    () =>
      [...profitLossEntries].sort((a, b) =>
        a.financialYearEnd.localeCompare(b.financialYearEnd),
      ),
    [profitLossEntries],
  );

  const plLabels = useMemo(
    () => sortedEntries.map((e) => e.period),
    [sortedEntries],
  );

  const revenueEarningsData = useMemo(
    () => ({
      labels: plLabels,
      datasets: [
        {
          label: "Revenue",
          data: sortedEntries.map((e) => e.revenue),
          borderColor: "#1F78B4",
          backgroundColor: "#1F78B4",
          pointBackgroundColor: "#1F78B4",
        },
        {
          label: "EBITDA",
          data: sortedEntries.map((e) => e.ebitda),
          borderColor: "#FF8B00",
          backgroundColor: "#FF8B00",
          pointBackgroundColor: "#FF8B00",
        },
        {
          label: "Profit before tax",
          data: sortedEntries.map((e) => e.currentProfit),
          borderColor: "#802392",
          backgroundColor: "#802392",
          pointBackgroundColor: "#802392",
        },
      ],
    }),
    [sortedEntries, plLabels],
  );

  const revenueMarginsData = useMemo(() => {
    const ratioByPeriod = new Map(ratioEntries.map((r) => [r.period, r]));
    const marginData = sortedEntries.map((e) => {
      const ratio = ratioByPeriod.get(e.period);
      const margin = ratio?.operatingMargin;
      return margin != null ? margin * 100 : null;
    });

    return {
      labels: plLabels,
      datasets: [
        {
          type: "bar" as const,
          label: "Revenue",
          data: sortedEntries.map((e) => e.revenue),
          borderColor: "#00BAB5",
          backgroundColor: "#00BAB5",
          yAxisID: "yLeft",
        },
        {
          type: "bar" as const,
          label: "EBITDA",
          data: sortedEntries.map((e) => e.ebitda),
          borderColor: "#F77E8A",
          backgroundColor: "#F77E8A",
          yAxisID: "yLeft",
        },
        {
          type: "line" as const,
          label: "EBIT margin %",
          data: marginData,
          borderColor: "#7c4cc4",
          backgroundColor: "#7c4cc4",
          pointBackgroundColor: "#7c4cc4",
          yAxisID: "yRight",
        },
      ],
    };
  }, [sortedEntries, ratioEntries, plLabels]);

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-lg border bg-card p-4">
        <h3 className="mb-4 text-subsection">Revenue & earnings</h3>
        <div style={{ height: 250 }}>
          <Line
            options={revenueEarningsOptions(currency)}
            data={revenueEarningsData}
          />
        </div>
      </div>
      {applyingCompanyId && hasBankingData && (
        <div className="rounded-lg border bg-card p-4">
          <h3 className="mb-4 text-subsection">Banking transactions</h3>
          <BankingDataChart
            resourceType="APPLYING_COMPANY"
            resourceId={applyingCompanyId}
            isMobile={false}
            primaryCurrency={currency}
          />
        </div>
      )}
    </div>
  );
};
